import { Link } from 'react-router-dom';
import { Alert, Button, Col, Descriptions, Progress, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
  MigrationError,
  MigrationStatus,
  MigrationStatusValue,
} from '../../../../../../../../API/TT_ELD/legalMigration';
import { queuedImportsFinished } from '../../../../../../../../Hooks/TT_ELD/LegalMigration';
import { DEFAULT_ELD, STANDARD_ELD } from '../../../../../../../../Utils/eldTypeLabels';

const { Text } = Typography;

export const DIRECTION_LABEL = {
  to_legal: `${DEFAULT_ELD} → ${STANDARD_ELD}`,
  to_non_legal: `${STANDARD_ELD} → ${DEFAULT_ELD}`,
};

const STATUS_TAG: Record<MigrationStatusValue, { color: string; label: string }> = {
  running: { color: 'processing', label: 'Running' },
  done: { color: 'success', label: 'Done' },
  partial: { color: 'warning', label: 'Partial' },
  failed: { color: 'error', label: 'Failed' },
};

const formatDate = (date?: string | null) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

const driverLink = (id: number) => <Link to={`/user/users/${id}`}>#{id}</Link>;

// Retry is allowed for partial / failed migrations, and for a "running" one that is only
// waiting on pending drivers while at least one queued driver failed
export const canRetry = (status: MigrationStatus) =>
  status.status === 'partial' ||
  status.status === 'failed' ||
  (status.status === 'running' && status.driversFailed > 0 && queuedImportsFinished(status));

const errorColumns = [
  {
    title: 'Driver',
    dataIndex: 'driverId',
    key: 'driverId',
    render: driverLink,
  },
  {
    title: 'Day',
    key: 'day',
    render: (error: MigrationError) =>
      error.date ?? (error.windowStart ? `Window from ${formatDate(error.windowStart)}` : <Tag>Whole import</Tag>),
  },
  { title: 'Message', dataIndex: 'message', key: 'message' },
];

interface Props {
  status: MigrationStatus;
  canManage: boolean;
  retrying: boolean;
  onRetry(): void;
}

const MigrationProgress = ({ status, canManage, retrying, onRetry }: Props) => {
  const processed = status.driversDone + status.driversFailed;
  const percent = status.driversTotal ? Math.round((processed / status.driversTotal) * 100) : 100;
  const waitingOnPendingOnly = status.status === 'running' && queuedImportsFinished(status);
  const pendingIds = status.pendingDriverIds ?? [];
  const tag = STATUS_TAG[status.status] ?? { color: 'default', label: status.status };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
        <Descriptions.Item label="Direction">{DIRECTION_LABEL[status.direction]}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={tag.color}>{tag.label}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Started">{formatDate(status.startedAt)}</Descriptions.Item>
        <Descriptions.Item label="Finished">{formatDate(status.finishedAt)}</Descriptions.Item>
      </Descriptions>

      <Progress
        percent={percent}
        success={{ percent: status.driversTotal ? (status.driversDone / status.driversTotal) * 100 : 100 }}
        status={status.status === 'running' ? 'active' : status.driversFailed ? 'exception' : 'success'}
      />

      <Row gutter={16}>
        <Col xs={12} sm={6}>
          <Statistic title="Drivers" value={status.driversTotal} />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic title="Done" value={status.driversDone} valueStyle={{ color: '#52c41a' }} />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Failed"
            value={status.driversFailed}
            valueStyle={{ color: status.driversFailed ? '#ff4d4f' : undefined }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic title="Pending" value={status.driversPending} />
        </Col>
      </Row>

      {status.status === 'running' && !waitingOnPendingOnly && (
        <Alert type="info" showIcon message="Logs are being imported in the background. This page updates automatically." />
      )}

      {status.driversPending > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${status.driversPending} driver${status.driversPending === 1 ? ' is' : 's are'} still pending`}
          description={
            <>
              <div>
                They were On Duty / Driving when the migration started and keep working as before. Each one
                switches automatically the next time they go Off Duty or Sleeper Berth in the app. The
                migration stays "Running" until they all switch.
              </div>
              {pendingIds.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <Space wrap size={[8, 4]}>
                    {pendingIds.map((id) => (
                      <span key={id}>{driverLink(id)}</span>
                    ))}
                  </Space>
                </div>
              )}
            </>
          }
        />
      )}

      {status.errors?.length > 0 && (
        <>
          <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
            <Text strong>Import errors ({status.errors.length})</Text>
            {canManage && canRetry(status) && (
              <Button icon={<ReloadOutlined />} loading={retrying} onClick={onRetry}>
                Retry failed drivers
              </Button>
            )}
          </Space>
          <Table
            size="small"
            rowKey={(error, index) => `${error.driverId}-${error.date ?? 'all'}-${index}`}
            dataSource={status.errors}
            columns={errorColumns}
            pagination={status.errors.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
            scroll={{ x: 'max-content' }}
          />
        </>
      )}
    </Space>
  );
};

export default MigrationProgress;
